import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChannelTypeTable } from './ChannelTypeTable';
import { Button } from '@/components/ui/button';
import { Plus, AlertTriangle } from 'lucide-react';
import { useState} from 'react';
import { ChannelTypeForm } from './ChannelTypeForm';
import type { VersionAwareProps } from '@/features/versions/types/props';
import { useProvider } from '@/hooks/useProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ChannelTypeModalProps extends Pick<VersionAwareProps, 'providerId' | 'versionId'> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChannelTypeModal({ 
  providerId, 
  versionId,
  open, 
  onOpenChange 
}: ChannelTypeModalProps) {
  const [isAddingType, setIsAddingType] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<number | null>(null);
  const { data: provider, } = useProvider(providerId);

  const handleSuccess = () => {
    setIsAddingType(false);
    setEditingTypeId(null);
  };

  const handleCancel = () => {
    setIsAddingType(false);
    setEditingTypeId(null);
  };

  const handleEdit = (typeId: number) => {
    setEditingTypeId(typeId);
  };

  const isSequentialProvider = provider?.has_sequence_numbers;

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          handleCancel();
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isAddingType ? 'Add Channel Type' : 
             editingTypeId ? 'Edit Channel Type' : 
             'Channel Types'}
          </DialogTitle>
          <DialogDescription>Description</DialogDescription>
        </DialogHeader>

        {isSequentialProvider && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This is a sequential provider. Channel types are managed automatically and cannot be added manually.
            </AlertDescription>
          </Alert>
        )}

        {isAddingType || editingTypeId ? (
          <ChannelTypeForm
            providerId={providerId}
            versionId={versionId}
            typeId={editingTypeId}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
          />
        ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              {!isSequentialProvider && (
                <Button onClick={() => setIsAddingType(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Type
                </Button>
              )}
            </div>
            
            <ChannelTypeTable 
              providerId={providerId}
              versionId={versionId}
              onEdit={handleEdit}
              isSequentialProvider={isSequentialProvider}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
